// apps/agents/src/features/gifts/GiftSelectionModal.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { useGiftsData } from './hooks/useGiftsData';
import GiftCard from './GiftCard';
import GiftBasket from './GiftBasket';

const GiftSelectionModal = ({ products, onComplete, onClose, existingPeriod }) => {
  // products = eligible products from cart (each has id, name, giftLevel field)
  const { giftItems, loading, error } = useGiftsData();
  
  const [selectedPeriod, setSelectedPeriod] = useState(existingPeriod || '');
  const [selectedGiftsByProduct, setSelectedGiftsByProduct] = useState({});
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [budgetError, setBudgetError] = useState(null);
  
  const currentProduct = products[currentProductIndex];
  const currentGiftLevel = currentProduct?.giftLevel || {};
  const currentCurrency = currentGiftLevel.currency || 'BHD';
  
  const getBudgetLimit = useCallback(() => {
    if (!selectedPeriod) return 0;
    return currentGiftLevel[selectedPeriod] || 0;
  }, [selectedPeriod, currentGiftLevel]);
  
  const currentProductSelectedGifts = selectedGiftsByProduct[currentProduct?.id] || [];
  
  const getSelectedGiftsTotal = useCallback(() => {
    return currentProductSelectedGifts.reduce((total, giftId) => {
      const gift = giftItems.find(g => g.id === giftId);
      return total + (gift?.prices?.[currentCurrency] || 0);
    }, 0);
  }, [currentProductSelectedGifts, giftItems, currentCurrency]);
  
  const selectedTotal = getSelectedGiftsTotal();
  const budgetLimit = getBudgetLimit();
  const remainingBudget = budgetLimit - selectedTotal;
  const isOverBudget = remainingBudget < 0;
  
  // Get selected gift objects for display
  const selectedGiftObjects = useMemo(() => {
    return currentProductSelectedGifts
      .map(giftId => giftItems.find(g => g.id === giftId))
      .filter(g => g);
  }, [currentProductSelectedGifts, giftItems]);
  
  // Only show gifts that fit within remaining budget (and not already selected)
  const availableGifts = useMemo(() => {
    if (!selectedPeriod) return [];
    
    const selectedIds = new Set(currentProductSelectedGifts);
    const setCode = currentProduct?.code;
    
    return giftItems.filter(gift => {
      // Block ANY gift where PntPlsssCode contains CKWSET (case-insensitive)
      if (gift.PntPlsssCode && /CKWSET/i.test(gift.PntPlsssCode)) {
        return false;
      }
      
      // Exclude components of the current set
      if (setCode && gift.PntPlsssCode) {
        const parentCodes = gift.PntPlsssCode.split(',').map(c => c.trim());
        if (parentCodes.includes(setCode)) return false;
      }

      const isSelected = selectedIds.has(gift.id);
      const giftPrice = gift.prices?.[currentCurrency] || 0;
      // Show if it's already selected, OR if it's affordable with the remaining budget
      return isSelected || giftPrice <= remainingBudget;
    });
  }, [giftItems, remainingBudget, selectedPeriod, currentCurrency, currentProductSelectedGifts, currentProduct]);
  
  const handlePeriodSelect = (period) => {
    setSelectedPeriod(period);
    setSelectedGiftsByProduct({});
    setCurrentProductIndex(0);
    setBudgetError(null);
  };
  
  const handleToggleGift = (gift) => {
    const productId = currentProduct.id;
    const currentSelected = selectedGiftsByProduct[productId] || [];
    const isSelected = currentSelected.includes(gift.id);
    
    if (isSelected) {
      // Remove gift
      setSelectedGiftsByProduct(prev => ({
        ...prev,
        [productId]: currentSelected.filter(id => id !== gift.id)
      }));
    } else {
      // Check budget before adding
      const giftPrice = gift.prices?.[currentCurrency] || 0;
      const currentTotal = getSelectedGiftsTotal();
      
      if (currentTotal + giftPrice <= budgetLimit) {
        setSelectedGiftsByProduct(prev => ({
          ...prev,
          [productId]: [...currentSelected, gift.id]
        }));
        setBudgetError(null);
      } else {
        setBudgetError(`Cannot add "${gift.name}". Would exceed ${budgetLimit} ${currentCurrency} budget limit.`);
        setTimeout(() => setBudgetError(null), 3000);
      }
    }
  };
  
  const handleRemoveFromBasket = (giftId) => {
    const productId = currentProduct.id;
    const currentSelected = selectedGiftsByProduct[productId] || [];
    setSelectedGiftsByProduct(prev => ({
      ...prev,
      [productId]: currentSelected.filter(id => id !== giftId)
    }));
  };
  
  const handleClearAllGifts = () => {
    const productId = currentProduct.id;
    setSelectedGiftsByProduct(prev => ({
      ...prev,
      [productId]: []
    }));
  };
  
  const handleNextProduct = () => {
    if (currentProductIndex < products.length - 1) {
      setCurrentProductIndex(prev => prev + 1);
    } else {
      // Complete - format output for Orders.jsx
      const selectedGiftsMap = {};
      Object.keys(selectedGiftsByProduct).forEach(productId => {
        const giftIds = selectedGiftsByProduct[productId];
        if (giftIds && giftIds.length > 0) {
          selectedGiftsMap[productId] = giftIds.join(' ');
        }
      });
      onComplete(selectedPeriod, selectedGiftsMap);
    }
  };
  
  const handlePreviousProduct = () => {
    if (currentProductIndex > 0) {
      setCurrentProductIndex(prev => prev - 1);
    }
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#19BFB7]"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md w-full">
          <div className="text-center">
            <p className="text-red-600 mb-4">Error loading gifts: {error}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Step 1: Select Payment Period
  if (!selectedPeriod) {
    const availablePeriods = currentProduct?.giftLevel 
      ? Object.keys(currentProduct.giftLevel)
          .filter(key => key !== 'currency' && currentProduct.giftLevel[key] > 0)
          .sort((a, b) => {
            const order = { day1: 1, day30: 2, day60: 3, day90: 4, day180: 5 };
            return (order[a] || 99) - (order[b] || 99);
          })
      : [];
    
    const periodLabels = {
      'day1': 'Pay full within 2 days',
      'day30': 'Pay full by end of month',
      'day60': 'Pay full within 2 months',
      'day90': 'Pay full within 3 months',
      'day180': 'Pay full within 6 months'
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#19BFB7] to-[#28E3DA] rounded-t-xl">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">Select Payment Period</h2>
                <p className="text-white/90 text-sm mt-1">{currentProduct?.name}</p>
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <p className="text-gray-600 mb-4">Choose a payment period to see available gifts:</p>
            <div className="space-y-3">
              {availablePeriods.map(period => (
                <button
                  key={period}
                  onClick={() => handlePeriodSelect(period)}
                  className="w-full p-4 text-left border-2 border-gray-200 rounded-lg hover:border-[#19BFB7] hover:bg-[#19BFB7]/5 transition-all"
                >
                  <div className="font-semibold text-gray-800">{periodLabels[period] || period}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Step 2: Select Gifts
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="bg-white w-full h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#19BFB7] to-[#28E3DA]">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-white">Select Free Gifts</h2>
              <p className="text-white/90 text-sm">
                {products.length > 1 
                  ? `Product ${currentProductIndex + 1} of ${products.length}: ${currentProduct?.name}`
                  : currentProduct?.name}
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-100">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Budget Info Bar removed to hide internal pricing from customers */}
          
          {/* Budget Error */}
          {budgetError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {budgetError}
            </div>
          )}
          
          {/* Over Budget Warning */}
          {isOverBudget && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
              ⚠️ You have exceeded your budget. Please remove some gifts from your basket below.
            </div>
          )}
          
          {/* Gift Basket - Shows selected gifts with Remove buttons */}
          <GiftBasket
            selectedGifts={selectedGiftObjects}
            onRemoveGift={handleRemoveFromBasket}
            onClearAll={handleClearAllGifts}
            cartTotalUSD={selectedGiftObjects.reduce((sum, g) => sum + (g.prices?.USD || 0), 0)}
            cartTotalBHD={selectedTotal}
            remainingBalance={remainingBudget}
            MAX_BUDGET={budgetLimit}
          />
          
          {/* Available Gifts Grid */}
          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-4">Available Gifts</h3>
            {availableGifts.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-4xl mb-3">🎁</div>
                <p className="text-gray-500">
                  {selectedGiftObjects.length > 0 
                    ? "You've reached your budget limit. Remove some gifts to add more."
                    : "No gifts available within your budget."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableGifts.map(gift => (
                  <GiftCard
                    key={gift.id}
                    gift={gift}
                    isSelected={currentProductSelectedGifts.includes(gift.id)}
                    onToggle={() => handleToggleGift(gift)}
                    selectedPeriod={selectedPeriod}
                    onPeriodChange={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between">
          <div>
            {currentProductIndex > 0 && (
              <button
                onClick={handlePreviousProduct}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ← Previous Product
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleNextProduct}
              disabled={isOverBudget}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                !isOverBudget
                  ? 'bg-gradient-to-r from-[#19BFB7] to-[#28E3DA] text-white hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {currentProductIndex < products.length - 1 ? 'Next Product →' : 'Complete Selection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftSelectionModal;
